import { z } from 'zod';
/**
 * Dashboards & Reports module contracts (advanced analytics, FR-80x).
 *
 * Reports and filters are query-driven: each persists an AQL `queryText` that
 * the shared report runner executes with the same permission filter as the
 * ticket list. Config blobs (columns, chart, layout) are kept as loose records
 * — mirroring SavedView.config — because they evolve with the UI, not the API.
 */
export const ReportVisibility = z.enum(['private', 'team', 'public']);
export const ReportViewType = z.enum(['grid', 'bar', 'line', 'pie', 'kpi']);
// --- Saved Reports ---------------------------------------------------------
export const SavedReport = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    ownerId: z.string().nullable(),
    ownerName: z.string().nullable().optional(),
    queryText: z.string(),
    basicFilters: z.record(z.unknown()).nullable(),
    columns: z.array(z.string()).nullable(),
    sort: z.record(z.unknown()).nullable(),
    groupBy: z.record(z.unknown()).nullable(),
    aggregation: z.record(z.unknown()).nullable(),
    chartConfig: z.record(z.unknown()).nullable(),
    defaultView: ReportViewType,
    visibility: ReportVisibility,
    tags: z.array(z.string()),
    favorite: z.boolean().optional(),
    canEdit: z.boolean().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    lastRunAt: z.string().nullable(),
});
export const CreateSavedReportInput = z.object({
    name: z.string().min(1),
    description: z.string().optional(),
    queryText: z.string().default(''),
    basicFilters: z.record(z.unknown()).optional(),
    columns: z.array(z.string()).optional(),
    sort: z.record(z.unknown()).optional(),
    groupBy: z.record(z.unknown()).optional(),
    aggregation: z.record(z.unknown()).optional(),
    chartConfig: z.record(z.unknown()).optional(),
    defaultView: ReportViewType.optional(),
    visibility: ReportVisibility.optional(),
    tags: z.array(z.string()).optional(),
});
export const UpdateSavedReportInput = CreateSavedReportInput.partial();
export const ShareReportInput = z.object({
    principalType: z.enum(['user', 'team', 'role', 'workspace']),
    principalId: z.string(),
    permission: z.enum(['view', 'edit', 'admin']).default('view'),
});
// --- Saved Filters ---------------------------------------------------------
export const SavedFilter = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    ownerId: z.string().nullable(),
    /** Owner's display name, denormalized for listing without a join. */
    ownerName: z.string().nullable().optional(),
    /** True when the current viewer owns this filter. */
    mine: z.boolean().optional(),
    queryText: z.string(),
    filters: z.record(z.unknown()).nullable(),
    visibility: ReportVisibility,
    tags: z.array(z.string()),
    favorite: z.boolean().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * A private filter owned by someone else, surfaced for discovery so a user can
 * request access. Intentionally redacted — no queryText/filters are exposed.
 */
export const DiscoverableFilter = z.object({
    id: z.string(),
    name: z.string(),
    ownerId: z.string().nullable(),
    ownerName: z.string().nullable(),
    visibility: ReportVisibility,
    createdAt: z.string(),
});
export const RequestFilterShareInput = z.object({ message: z.string().max(500).optional() });
export const CreateSavedFilterInput = z.object({
    name: z.string().min(1),
    description: z.string().optional(),
    queryText: z.string().default(''),
    filters: z.record(z.unknown()).optional(),
    visibility: ReportVisibility.optional(),
    tags: z.array(z.string()).optional(),
});
export const UpdateSavedFilterInput = CreateSavedFilterInput.partial();
// --- Dashboards & widgets --------------------------------------------------
export const WidgetType = z.enum([
    'kpi', 'bar', 'line', 'pie', 'query_grid', 'cfd', 'velocity', 'burndown', 'notes',
]);
export const DashboardWidget = z.object({
    id: z.string(),
    dashboardId: z.string(),
    widgetType: WidgetType,
    title: z.string().nullable(),
    description: z.string().nullable(),
    dataSource: z.enum(['aql', 'savedReport', 'builtin']),
    query: z.string().nullable(),
    reportId: z.string().nullable(),
    chartConfig: z.record(z.unknown()).nullable(),
    displayConfig: z.record(z.unknown()).nullable(),
    position: z.record(z.unknown()).nullable(),
    refreshSeconds: z.number().nullable(),
});
export const Dashboard = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    ownerId: z.string().nullable(),
    ownerName: z.string().nullable().optional(),
    visibility: ReportVisibility,
    layout: z.array(z.record(z.unknown())).nullable(),
    globalFilters: z.record(z.unknown()).nullable(),
    favorite: z.boolean().optional(),
    canEdit: z.boolean().optional(),
    widgets: z.array(DashboardWidget).optional(),
    lastViewedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateDashboardInput = z.object({
    name: z.string().min(1),
    description: z.string().optional(),
    visibility: ReportVisibility.optional(),
    layout: z.array(z.record(z.unknown())).optional(),
    globalFilters: z.record(z.unknown()).optional(),
});
export const UpdateDashboardInput = CreateDashboardInput.partial();
export const CreateWidgetInput = z.object({
    widgetType: WidgetType,
    title: z.string().optional(),
    description: z.string().optional(),
    dataSource: z.enum(['aql', 'savedReport', 'builtin']).default('aql'),
    query: z.string().optional(),
    reportId: z.string().optional(),
    chartConfig: z.record(z.unknown()).optional(),
    displayConfig: z.record(z.unknown()).optional(),
    position: z.record(z.unknown()).optional(),
    refreshSeconds: z.number().optional(),
});
export const UpdateWidgetInput = CreateWidgetInput.partial();
// --- Execution (run / preview / widget run) --------------------------------
/** Ad-hoc run of an AQL query (report preview, widget run, drill-down). */
export const ReportRunInput = z.object({
    queryText: z.string().default(''),
    page: z.number().int().positive().optional(),
    pageSize: z.number().int().positive().max(500).optional(),
    /** Override the compiled aggregation (used by dashboard widgets). */
    groupBy: z.string().optional(),
    bucket: z.enum(['day', 'week', 'month']).optional(),
    metric: z.enum(['count', 'sum', 'avg']).optional(),
    metricField: z.string().optional(),
});
/** A single aggregation bucket (bar/pie/line/kpi data point). */
export const ReportBucket = z.object({
    key: z.string(),
    count: z.number(),
});
//# sourceMappingURL=reports.js.map